import { Component, type ErrorInfo, type ReactNode } from 'react';
import { trackError, ErrorSeverity, type ErrorContext } from '../../utils/error-tracking';

interface RowErrorBoundaryProps {
  children: ReactNode;
  /** Identifies the failing row in the error report, e.g. `tag:vintage`. */
  rowName: string;
  /** Component doing the wrapping, e.g. `ForYouPage`. */
  component?: string;
}

interface RowErrorBoundaryState {
  hasError: boolean;
}

/**
 * Drops a single feed row when it throws, instead of letting it take the page
 * down with it. Infinite feeds render dozens of independent rows, each holding a
 * Swiper and third-party ad markup that mutates its own DOM — one reconciliation
 * error there used to unmount the whole app and leave a blank page.
 *
 * Deliberately does NOT retry: a row that fails on render fails again on the
 * next one, so re-rendering it would spin. It reports once and stays gone until
 * the page reloads. Use SilentErrorBoundary instead where recovery is plausible.
 */
export class RowErrorBoundary extends Component<RowErrorBoundaryProps, RowErrorBoundaryState> {
  state: RowErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RowErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { rowName, component = 'FeedRow' } = this.props;
    const context: ErrorContext = {
      component,
      boundary: 'row',
      componentStack: errorInfo.componentStack,
      tags: {
        errorBoundary: 'row',
        componentName: component,
        row: rowName,
      },
    };

    trackError(error, ErrorSeverity.ERROR, context);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export default RowErrorBoundary;
